import { useEffect, useState } from "react";
import AssistantChatPanel from "@/components/AssistantChatPanel";
import AssistantFloatingTrigger from "@/components/AssistantFloatingTrigger";
import AssistantShowcaseSection from "@/components/AssistantShowcaseSection";
import "@/Assistant.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "liberdade-assistant-session";
const initialMessage = { role: "assistant", text: "Olá. Sou o Guia do Leitor, uma IA de apoio educativo. Posso explicar o livro e ajudar você a escolher um próximo passo pequeno — sem julgamentos e sem prometer milagres." };

function getSessionId() {
  const saved = window.localStorage.getItem(SESSION_KEY);
  if (saved) return saved;
  const created = window.crypto?.randomUUID?.() || `reader-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  window.localStorage.setItem(SESSION_KEY, created);
  return created;
}

export default function AssistantChat() {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([initialMessage]);

  useEffect(() => {
    const closeOnEscape = (event) => event.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, []);

  const openAssistant = (suggestion = "") => {
    setDraft(suggestion);
    setOpen(true);
  };

  const replaceLastAssistant = (text) => setMessages((current) => [...current.slice(0, -1), { role: "assistant", text }]);

  const send = async (text) => {
    const message = text.trim();
    if (!message || busy) return;
    setDraft("");
    setBusy(true);
    setMessages((current) => [...current, { role: "user", text: message }, { role: "assistant", text: "" }]);
    try {
      const response = await fetch(`${API}/assistant/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: getSessionId(), message }) });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || "O Guia do Leitor está indisponível agora.");
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const event of events) {
          const line = event.split("\n").find((item) => item.startsWith("data: "));
          if (!line) continue;
          const payload = JSON.parse(line.slice(6));
          if (payload.type === "delta") setMessages((current) => [...current.slice(0, -1), { role: "assistant", text: `${current[current.length - 1]?.text || ""}${payload.content}` }]);
          if (payload.type === "error") throw new Error(payload.message);
        }
        if (done) break;
      }
    } catch (error) {
      replaceLastAssistant(error.message || "Não consegui responder agora. Tente novamente em instantes.");
    } finally {
      setBusy(false);
    }
  };

  return <><AssistantShowcaseSection onOpen={openAssistant} /><AssistantFloatingTrigger onClick={openAssistant} /><AssistantChatPanel open={open} onClose={() => setOpen(false)} messages={messages} draft={draft} setDraft={setDraft} onSend={send} busy={busy} /></>;
}
